import React, { useState } from 'react';
import { styles, styleVars } from './CarouselTabs.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { Swiper } from 'swiper/types';
import { CarouselProps } from '@jbhi-fi/lanyard-ui/components/Carousel/Carousel';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';

export type Tab = {
  renderLabel: (tabProps: {
    testId?: string;
    isActive: boolean;
    onClick: (event: React.MouseEvent) => void;
  }) => React.ReactNode;
  contents: React.ReactNode;
  labelTestId?: string;
  contentsTestId?: string;
};

export type CarouselTabsProps = {
  label?: string;
  tabs: Array<Tab>;
  textColor?: React.CSSProperties['color'];
  borderBottomColor?: React.CSSProperties['color'];
  tabsContainerClassName?: string;
  initialTabIndex?: number | null;
  canBeUnselected?: boolean;
  testId?: string;
} & Omit<Partial<CarouselProps>, 'children' | 'className'>;

export const CarouselTabs = ({
  tabs,
  label,
  textColor = 'black',
  borderBottomColor = textColor,
  tabsContainerClassName,
  initialTabIndex,
  canBeUnselected = false,
  testId,
  ...carouselProps
}: CarouselTabsProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number | undefined>(
    initialTabIndex === null ? undefined : initialTabIndex ?? 0
  );
  const [swiper, setSwiper] = useState<Swiper | null>(null);
  const activeTab = activeTabIndex !== undefined ? tabs[activeTabIndex] : null;

  const handleTabClick = (tabIndex: number) => {
    if (activeTabIndex === tabIndex && canBeUnselected) {
      setActiveTabIndex(undefined);
    } else {
      setActiveTabIndex(tabIndex);
      if (!swiper?.isLocked) {
        /* Slide to active slide unless all slides are in view */
        swiper?.slideTo(tabIndex);
      }
    }
  };

  return (
    <div style={assignInlineVars({ [styleVars.textColor]: textColor })} className={styles.wrapper}>
      <div className={classNames(styles.tabsContainer, tabsContainerClassName)}>
        <Carousel
          showPagination={false}
          showArrows={false}
          showProgressBar={false}
          onSwiper={setSwiper}
          slidesPerView="auto"
          className={styles.container}
          style={{ boxShadow: `inset 0 -2px 0 ${borderBottomColor}` }}
          arrowPosition="bottom"
          {...carouselProps}
        >
          {[
            label ? (
              <p key="label" className={styles.label}>
                {label}
              </p>
            ) : undefined,
          ]
            .concat(
              tabs.map(({ renderLabel, labelTestId }, i) => (
                <span key={i} className={styles.tab} data-testid={labelTestId}>
                  {renderLabel({
                    testId: `${testId}-tab-${i + 1}`,
                    isActive: i === activeTabIndex,
                    onClick: () => handleTabClick(i),
                  })}
                </span>
              ))
            )
            .filter(Boolean)}
        </Carousel>
      </div>
      <div key={activeTabIndex} data-testid={activeTab?.contentsTestId}>
        {activeTab?.contents ?? null}
      </div>
    </div>
  );
};
